import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import api from '@/lib/api';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import QRCode from 'react-qr-code';
import { 
  Stamp, QrCode, ScanLine, Gift, Users, Loader2, Save, Check, X, Camera,
  Heart, Star, Coffee, Pizza, Flame, Diamond, Palette, Crown, Leaf, Zap,
  Trophy, Music, Sun, Cake, Flower2, Snowflake, Scissors, Wine, IceCreamCone,
  Dog, Car, Wrench, Book, Anchor, MapPin, Mountain, TreePine, Waves, Compass,
  Sparkles, ShoppingBag, Shirt, Dumbbell, Stethoscope, GraduationCap, Gamepad2, Headphones, Brush,
  Share2, Copy, Link
} from 'lucide-react';

// Themes - predefined color + icon combos
const THEMES = {
  activity: [
    { id: 'cafe', label: 'Café', icon: Coffee, color: '#795548' },
    { id: 'restaurant', label: 'Restaurant', icon: Pizza, color: '#d32f2f' },
    { id: 'boulangerie', label: 'Boulangerie', icon: Cake, color: '#8d6e63' },
    { id: 'coiffure', label: 'Coiffure', icon: Scissors, color: '#9c27b0' },
    { id: 'boutique', label: 'Boutique', icon: ShoppingBag, color: '#e91e63' },
    { id: 'sport', label: 'Sport', icon: Dumbbell, color: '#4caf50' },
    { id: 'bienetre', label: 'Bien-être', icon: Heart, color: '#ff69b4' },
    { id: 'fleuriste', label: 'Fleuriste', icon: Flower2, color: '#66bb6a' },
    { id: 'pizzeria', label: 'Pizzeria', icon: Pizza, color: '#c62828' },
    { id: 'bar', label: 'Bar', icon: Wine, color: '#5d4037' },
    { id: 'glacier', label: 'Glacier', icon: IceCreamCone, color: '#00bcd4' },
    { id: 'animalerie', label: 'Animalerie', icon: Dog, color: '#009688' },
    { id: 'garage', label: 'Garage', icon: Car, color: '#455a64' },
    { id: 'librairie', label: 'Librairie', icon: Book, color: '#3f51b5' },
    { id: 'musique', label: 'Musique', icon: Music, color: '#7c4dff' },
    { id: 'sante', label: 'Santé', icon: Stethoscope, color: '#00897b' },
  ],
  ambiance: [
    { id: 'luxe', label: 'Luxe', icon: Crown, color: '#1a1a2e' },
    { id: 'nature', label: 'Nature', icon: Leaf, color: '#2e7d32' },
    { id: 'retro', label: 'Retro', icon: Sun, color: '#bf360c' },
    { id: 'moderne', label: 'Moderne', icon: Zap, color: '#263238' },
    { id: 'festif', label: 'Festif', icon: Sparkles, color: '#7b1fa2' },
    { id: 'minimaliste', label: 'Minimaliste', icon: Diamond, color: '#546e7a' },
    { id: 'tropical', label: 'Tropical', icon: Sun, color: '#e65100' },
    { id: 'montagne', label: 'Montagne', icon: Mountain, color: '#37474f' },
    { id: 'maritime', label: 'Maritime', icon: Anchor, color: '#1565c0' },
    { id: 'aventure', label: 'Aventure', icon: Compass, color: '#6d4c41' },
  ]
};

// Stamp icons
const STAMP_ICONS = [
  { id: 'stamp', label: 'Classique', Icon: Stamp },
  { id: 'star', label: 'Etoile', Icon: Star },
  { id: 'heart', label: 'Coeur', Icon: Heart },
  { id: 'diamond', label: 'Diamant', Icon: Diamond },
  { id: 'coffee', label: 'Café', Icon: Coffee },
  { id: 'leaf', label: 'Feuille', Icon: Leaf },
  { id: 'crown', label: 'Couronne', Icon: Crown },
  { id: 'flower', label: 'Fleur', Icon: Flower2 },
  { id: 'zap', label: 'Eclair', Icon: Zap },
  { id: 'flame', label: 'Flamme', Icon: Flame },
  { id: 'trophy', label: 'Trophée', Icon: Trophy },
  { id: 'dog', label: 'Patte', Icon: Dog },
  { id: 'music', label: 'Musique', Icon: Music },
  { id: 'pizza', label: 'Pizza', Icon: Pizza },
  { id: 'cake', label: 'Gâteau', Icon: Cake },
  { id: 'sun', label: 'Soleil', Icon: Sun },
  { id: 'snowflake', label: 'Flocon', Icon: Snowflake },
  { id: 'check', label: 'Check', Icon: Check },
  { id: 'sparkles', label: 'Etincelle', Icon: Sparkles },
];

// Card icons (header icon)
const CARD_ICONS = [
  Coffee, Pizza, Cake, Scissors, ShoppingBag, Dumbbell, Heart, Flower2,
  Wine, IceCreamCone, Dog, Car, Book, Music, Stethoscope, Crown,
  Leaf, Zap, Sparkles, Gift, Star, Diamond, Flame, Trophy,
  Sun, Mountain, Anchor, Compass, Wrench, Shirt, GraduationCap,
  Gamepad2, Headphones, Brush, Snowflake, TreePine, Waves, MapPin, Stamp
];

const CARD_ICON_IDS = [
  'coffee', 'pizza', 'cake', 'scissors', 'shoppingbag', 'dumbbell', 'heart', 'flower',
  'wine', 'icecream', 'dog', 'car', 'book', 'music', 'stethoscope', 'crown',
  'leaf', 'zap', 'sparkles', 'gift', 'star', 'diamond', 'flame', 'trophy',
  'sun', 'mountain', 'anchor', 'compass', 'wrench', 'shirt', 'graduation',
  'gamepad', 'headphones', 'brush', 'snowflake', 'treepine', 'waves', 'mappin', 'stamp'
];

// Colors - large palette
const CARD_COLORS = [
  '#059669', '#3b82f6', '#7c3aed', '#f97316',
  '#dc2626', '#b45309', '#1e293b', '#0891b2',
  '#e11d48', '#d97706', '#374151', '#0d9488',
  '#4f46e5', '#be185d', '#15803d', '#7e22ce',
  '#0369a1', '#c2410c', '#4a5568', '#065f46',
];

// Patterns
const CARD_PATTERNS = [
  { id: 'none', label: 'Aucun' },
  { id: 'cercles', label: 'Cercles' },
  { id: 'points', label: 'Points' },
  { id: 'vagues', label: 'Vagues' },
  { id: 'diagonales', label: 'Diagonales' },
  { id: 'damier', label: 'Damier' },
  { id: 'grille', label: 'Grille' },
  { id: 'zigzag', label: 'Zigzag' },
  { id: 'confettis', label: 'Confettis' },
  { id: 'rayures', label: 'Rayures' },
  { id: 'losanges', label: 'Losanges' },
  { id: 'triangles', label: 'Triangles' },
  { id: 'croix', label: 'Croix' },
  { id: 'hexagones', label: 'Hexagones' },
  { id: 'etoiles', label: 'Etoiles' },
  { id: 'bulles', label: 'Bulles' },
];

const getPatternCSS = (pattern) => {
  const w = 'rgba(255,255,255,';
  switch (pattern) {
    case 'cercles': return { backgroundImage: `radial-gradient(circle, transparent 7px, ${w}0.08) 7px, ${w}0.08) 8px, transparent 8px)`, backgroundSize: '22px 22px' };
    case 'points': return { backgroundImage: `radial-gradient(${w}0.15) 2px, transparent 2px)`, backgroundSize: '12px 12px' };
    case 'vagues': return { backgroundImage: `repeating-linear-gradient(45deg, ${w}0.07) 0px, ${w}0.07) 1px, transparent 1px, transparent 10px), repeating-linear-gradient(-45deg, ${w}0.07) 0px, ${w}0.07) 1px, transparent 1px, transparent 10px)` };
    case 'diagonales': return { backgroundImage: `repeating-linear-gradient(45deg, ${w}0.1) 0, ${w}0.1) 1px, transparent 1px, transparent 8px)` };
    case 'damier': return { backgroundImage: `linear-gradient(45deg, ${w}0.08) 25%, transparent 25%), linear-gradient(-45deg, ${w}0.08) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${w}0.08) 75%), linear-gradient(-45deg, transparent 75%, ${w}0.08) 75%)`, backgroundSize: '16px 16px', backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px' };
    case 'grille': return { backgroundImage: `linear-gradient(${w}0.08) 1px, transparent 1px), linear-gradient(90deg, ${w}0.08) 1px, transparent 1px)`, backgroundSize: '20px 20px' };
    case 'zigzag': return { backgroundImage: `linear-gradient(135deg, ${w}0.1) 25%, transparent 25%), linear-gradient(225deg, ${w}0.1) 25%, transparent 25%), linear-gradient(315deg, ${w}0.1) 25%, transparent 25%), linear-gradient(45deg, ${w}0.1) 25%, transparent 25%)`, backgroundSize: '20px 20px', backgroundPosition: '0 0, 10px 0, 10px -10px, 0 10px' };
    case 'confettis': return { backgroundImage: `radial-gradient(${w}0.12) 1.5px, transparent 1.5px), radial-gradient(${w}0.12) 1.5px, transparent 1.5px)`, backgroundSize: '16px 16px', backgroundPosition: '0 0, 8px 8px' };
    case 'rayures': return { backgroundImage: `repeating-linear-gradient(90deg, ${w}0.08) 0, ${w}0.08) 2px, transparent 2px, transparent 10px)` };
    case 'losanges': return { backgroundImage: `linear-gradient(45deg, ${w}0.08) 25%, transparent 25%), linear-gradient(-45deg, ${w}0.08) 25%, transparent 25%)`, backgroundSize: '16px 16px' };
    case 'triangles': return { backgroundImage: `linear-gradient(60deg, ${w}0.07) 25%, transparent 25%), linear-gradient(-60deg, ${w}0.07) 25%, transparent 25%)`, backgroundSize: '18px 14px' };
    case 'croix': return { backgroundImage: `linear-gradient(${w}0.1) 2px, transparent 2px), linear-gradient(90deg, ${w}0.1) 2px, transparent 2px)`, backgroundSize: '12px 12px', backgroundPosition: 'center center' };
    case 'hexagones': return { backgroundImage: `radial-gradient(circle farthest-side at 50% 50%, transparent 60%, ${w}0.08) 60%, ${w}0.08) 65%, transparent 65%)`, backgroundSize: '18px 18px' };
    case 'etoiles': return { backgroundImage: `radial-gradient(${w}0.15) 1px, transparent 1px), radial-gradient(${w}0.1) 1px, transparent 1px)`, backgroundSize: '10px 10px', backgroundPosition: '0 0, 5px 5px' };
    case 'bulles': return { backgroundImage: `radial-gradient(circle, ${w}0.08) 4px, transparent 4px), radial-gradient(circle, ${w}0.05) 3px, transparent 3px)`, backgroundSize: '24px 24px', backgroundPosition: '0 0, 12px 12px' };
    default: return {};
  }
};

const getStampIconComponent = (iconId) => {
  const found = STAMP_ICONS.find(i => i.id === iconId);
  return found ? found.Icon : Stamp;
};

const getCardIconComponent = (iconId) => {
  const idx = CARD_ICON_IDS.indexOf(iconId);
  return idx >= 0 ? CARD_ICONS[idx] : Gift;
};

const LoyaltyManagement = () => {
  const [config, setConfig] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantSlug, setRestaurantSlug] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [configForm, setConfigForm] = useState({
    stamps_required: 10, reward_text: '1 plat offert', active: true,
    logo_url: '', stamp_icon: 'stamp', card_icon: 'gift', card_color: '#059669', card_pattern: 'none'
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [stampResult, setStampResult] = useState(null);
  const [activeTab, setActiveTab] = useState('config');
  const [manualCode, setManualCode] = useState('');
  const [stamping, setStamping] = useState(false);
  const html5QrCodeRef = useRef(null);

  useEffect(() => { fetchData(); return () => stopScanner(); }, []);

  const fetchData = async () => {
    try {
      const [configRes, clientsRes] = await Promise.all([api.get('/loyalty/config'), api.get('/loyalty/clients')]);
      if (configRes.data?.restaurant_name) setRestaurantName(configRes.data.restaurant_name);
      if (configRes.data?.restaurant_slug) setRestaurantSlug(configRes.data.restaurant_slug);
      if (configRes.data?.id) {
        setConfig(configRes.data);
        setConfigForm({
          stamps_required: configRes.data.stamps_required, reward_text: configRes.data.reward_text,
          active: configRes.data.active, logo_url: configRes.data.logo_url || '',
          stamp_icon: configRes.data.stamp_icon || 'stamp', card_icon: configRes.data.card_icon || 'gift',
          card_color: configRes.data.card_color || '#059669', card_pattern: configRes.data.card_pattern || 'none'
        });
      }
      setClients(clientsRes.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const saveConfig = async () => {
    if (!configForm.reward_text.trim()) { toast.error('La récompense est requise'); return; }
    setSaving(true);
    try {
      const res = await api.post('/loyalty/config', configForm);
      setConfig(res.data);
      toast.success('Carte de fidélité configurée !');
    } catch (e) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const applyTheme = (theme) => {
    const iconId = CARD_ICON_IDS[CARD_ICONS.indexOf(theme.icon)] || 'gift';
    setConfigForm(prev => ({ ...prev, card_color: theme.color, card_icon: iconId }));
  };

  const startScanner = async () => {
    setScanning(true); setStampResult(null);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 250, height: 250 } },
        async (txt) => { try { const d = JSON.parse(txt); if (d.client_id) { await stopScanner(); await addStamp(d.client_id); } } catch {} }, () => {});
    } catch { toast.error("Impossible d'accéder à la caméra"); setScanning(false); }
  };

  const stopScanner = async () => {
    try { if (html5QrCodeRef.current) { await html5QrCodeRef.current.stop(); html5QrCodeRef.current = null; } } catch {}
    setScanning(false);
  };

  const addStamp = async (clientId) => {
    try { const res = await api.post('/loyalty/stamp', { client_id: clientId }); setStampResult(res.data); toast.success(res.data.message); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); setStampResult(null); }
  };

  const handleManualStamp = async () => {
    if (!manualCode.trim()) return;
    setStamping(true);
    try {
      const res = await api.post('/loyalty/stamp', { client_id: manualCode.trim() });
      setStampResult(res.data);
      toast.success(res.data.message);
      setManualCode('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Client non trouvé');
    } finally { setStamping(false); }
  };

  const color = configForm.card_color;
  const patternStyle = getPatternCSS(configForm.card_pattern);
  const StampIcon = getStampIconComponent(configForm.stamp_icon);
  const CardIcon = getCardIconComponent(configForm.card_icon);

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6" data-testid="loyalty-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Stamp className="h-8 w-8 text-orange-500" />
          Carte de fidélité
        </h1>
        <p className="text-gray-500 mt-1">Configurez et gérez votre programme de fidélité à tampons</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-1">
        {[
          { id: 'config', label: 'Personnalisation', icon: Palette },
          { id: 'scanner', label: 'Scanner', icon: ScanLine },
          { id: 'clients', label: `Clients (${clients.length})`, icon: Users },
          { id: 'partage', label: 'Partage', icon: Share2 },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab.id ? 'bg-white text-orange-600 border border-b-white border-gray-200 -mb-px' : 'text-gray-500 hover:text-gray-700'
            }`} data-testid={`tab-${tab.id}`}>
            <tab.icon className="h-4 w-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* CONFIG TAB */}
      {activeTab === 'config' && (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-5">
            {/* Settings */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Réglages</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre de tampons</Label>
                    <Input type="number" min="3" max="20" value={configForm.stamps_required}
                      onChange={(e) => setConfigForm(prev => ({ ...prev, stamps_required: parseInt(e.target.value) || 10 }))} data-testid="stamps-required-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Récompense</Label>
                    <Input value={configForm.reward_text} onChange={(e) => setConfigForm(prev => ({ ...prev, reward_text: e.target.value }))}
                      placeholder="Ex: 1 burger offert" data-testid="reward-text-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Logo (optionnel)</Label>
                  <ImageUpload value={configForm.logo_url} onChange={(url) => setConfigForm(prev => ({ ...prev, logo_url: url }))} aspectRatio="square" placeholder="PNG, JPG ou SVG" />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Carte active</Label>
                  <Switch checked={configForm.active} onCheckedChange={(v) => setConfigForm(prev => ({ ...prev, active: v }))} data-testid="loyalty-active-switch" />
                </div>
              </CardContent>
            </Card>

            {/* Themes */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Thèmes</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Thèmes activité</p>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {THEMES.activity.map(t => (
                    <button key={t.id} onClick={() => applyTheme(t)}
                      className="flex flex-col items-center gap-1 p-2 rounded-xl transition-all hover:scale-105"
                      style={{ backgroundColor: t.color }} data-testid={`theme-${t.id}`}>
                      <t.icon className="h-5 w-5 text-white" />
                      <span className="text-[9px] text-white/80 font-medium">{t.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider pt-2">Thèmes ambiance</p>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {THEMES.ambiance.map(t => (
                    <button key={t.id} onClick={() => applyTheme(t)}
                      className="flex flex-col items-center gap-1 p-2 rounded-xl transition-all hover:scale-105"
                      style={{ backgroundColor: t.color }} data-testid={`theme-${t.id}`}>
                      <t.icon className="h-5 w-5 text-white" />
                      <span className="text-[9px] text-white/80 font-medium">{t.label}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Stamp style */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Style des tampons</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {STAMP_ICONS.map(({ id, label, Icon }) => (
                    <button key={id} onClick={() => setConfigForm(prev => ({ ...prev, stamp_icon: id }))}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-all ${
                        configForm.stamp_icon === id ? 'text-white font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`} style={configForm.stamp_icon === id ? { backgroundColor: color } : {}} data-testid={`stamp-${id}`}>
                      <Icon className="h-4 w-4" />{label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Card icon */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Icône de la carte</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-8 gap-2">
                  {CARD_ICONS.map((Icon, idx) => (
                    <button key={idx} onClick={() => setConfigForm(prev => ({ ...prev, card_icon: CARD_ICON_IDS[idx] }))}
                      className={`aspect-square rounded-xl flex items-center justify-center transition-all ${
                        configForm.card_icon === CARD_ICON_IDS[idx] ? 'text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`} style={configForm.card_icon === CARD_ICON_IDS[idx] ? { backgroundColor: color } : {}} data-testid={`cicon-${CARD_ICON_IDS[idx]}`}>
                      <Icon className="h-5 w-5" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Colors */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Couleurs</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {CARD_COLORS.map(c => (
                    <button key={c} onClick={() => setConfigForm(prev => ({ ...prev, card_color: c }))}
                      className={`h-12 rounded-xl transition-all ${configForm.card_color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-105' : 'hover:scale-105'}`}
                      style={{ backgroundColor: c }} data-testid={`color-${c}`} />
                  ))}
                  <div className="h-12 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden relative">
                    <input type="color" value={configForm.card_color} onChange={(e) => setConfigForm(prev => ({ ...prev, card_color: e.target.value }))}
                      className="absolute inset-0 opacity-0 cursor-pointer" />
                    <Palette className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patterns */}
            <Card className="border-0 shadow-soft">
              <CardHeader className="pb-3"><CardTitle className="font-heading text-lg">Motif de fond</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-2">
                  {CARD_PATTERNS.map(({ id, label }) => (
                    <button key={id} onClick={() => setConfigForm(prev => ({ ...prev, card_pattern: id }))}
                      className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        configForm.card_pattern === id ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`} style={configForm.card_pattern === id ? { backgroundColor: color } : {}} data-testid={`pattern-${id}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Button onClick={saveConfig} disabled={saving} className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base" data-testid="save-loyalty-config">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {config ? 'Mettre à jour la carte' : 'Créer la carte +'}
            </Button>
          </div>

          {/* Live Preview */}
          <div className="lg:col-span-2">
            <div className="sticky top-6">
              <p className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Aperçu en direct</p>
              <div className="rounded-3xl overflow-hidden shadow-2xl relative" style={{ backgroundColor: color, ...patternStyle }}>
                <div className="px-5 pt-5 pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {configForm.logo_url ? (
                        <img src={configForm.logo_url} alt="" className="h-10 w-10 rounded-xl object-cover bg-white/20" />
                      ) : (
                        <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center">
                          <CardIcon className="h-5 w-5 text-white/70" />
                        </div>
                      )}
                      <div>
                        <p className="text-[9px] font-semibold text-white/50 uppercase tracking-widest">Carte fidélité</p>
                        <p className="font-bold text-white text-lg leading-tight">{restaurantName || 'Mon restaurant'}</p>
                        <p className="text-white/50 text-xs">Entreprise</p>
                      </div>
                    </div>
                    <div className="px-2.5 py-1 rounded-lg bg-white/15 text-white text-[9px] font-bold uppercase tracking-wider">
                      Tampons
                    </div>
                  </div>
                </div>
                <div className="px-5 pb-4 flex gap-3">
                  <div className="flex-shrink-0 bg-white rounded-xl p-2 shadow-lg">
                    <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center">
                      <QrCode className="h-8 w-8 text-gray-300" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {Array.from({ length: configForm.stamps_required }).map((_, i) => (
                        <div key={i} className="h-6 w-6 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: i < 3 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.15)' }}>
                          {i < 3 && <StampIcon className="h-3 w-3" style={{ color }} />}
                        </div>
                      ))}
                    </div>
                    <p className="text-white/70 text-xs font-medium italic">3/{configForm.stamps_required} tampons</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Gift className="h-3 w-3 text-white/50" />
                      <p className="text-white/50 text-[10px]">{configForm.reward_text}</p>
                    </div>
                  </div>
                </div>
                <div className="mx-5 border-t border-white/10" />
                <div className="px-5 py-2.5 flex items-center justify-between">
                  <span className="text-white/30 text-[9px] font-mono">QR-XXXX</span>
                  <span className="text-white/25 text-[9px] font-semibold">ZOTMENU</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SCANNER TAB */}
      {activeTab === 'scanner' && (
        <Card className="border-0 shadow-soft max-w-lg mx-auto" data-testid="loyalty-scanner">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Code QR du client</CardTitle>
            <CardDescription>Scannez avec la caméra ou entrez le code manuellement</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {!config?.active ? (
              <div className="text-center py-8 text-gray-500"><Stamp className="h-12 w-12 mx-auto mb-3 text-gray-300" /><p>Activez d'abord votre carte de fidélité</p></div>
            ) : stampResult ? (
              <div className={`rounded-xl p-5 text-center ${stampResult.completed ? 'bg-emerald-50 border border-emerald-200' : 'bg-orange-50 border border-orange-200'}`}>
                <div className="h-14 w-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: stampResult.completed ? '#10b981' : color }}>
                  {stampResult.completed ? <Gift className="h-7 w-7 text-white" /> : <StampIcon className="h-7 w-7 text-white" />}
                </div>
                <p className="font-semibold text-lg" style={{ color: stampResult.completed ? '#10b981' : color }}>{stampResult.message}</p>
                {!stampResult.completed && <p className="text-sm text-gray-500 mt-1">{stampResult.stamps}/{stampResult.stamps_required} tampons</p>}
                <Button onClick={() => setStampResult(null)} className="mt-4 bg-gray-900 hover:bg-gray-800 text-white" data-testid="scan-another-btn">
                  <ScanLine className="h-4 w-4 mr-2" />Scanner un autre client
                </Button>
              </div>
            ) : (
              <>
                {/* Camera scanner */}
                <div id="qr-reader" className={`rounded-xl overflow-hidden bg-gray-900 ${scanning ? 'min-h-[280px]' : 'hidden'}`} />
                {!scanning ? (
                  <Button onClick={startScanner} className="w-full h-14 rounded-xl text-white text-lg font-semibold" style={{ backgroundColor: color }} data-testid="start-scanner-btn">
                    <Camera className="h-5 w-5 mr-2" />Scanner avec la caméra
                  </Button>
                ) : (
                  <Button onClick={stopScanner} variant="outline" className="w-full"><X className="h-4 w-4 mr-2" />Fermer la caméra</Button>
                )}

                {/* Separator */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-gray-200" />
                  <span className="text-sm text-gray-400">ou saisir manuellement</span>
                  <div className="flex-1 border-t border-gray-200" />
                </div>

                {/* Manual input */}
                <div className="flex gap-2">
                  <Input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value)}
                    placeholder="Email du client ou code"
                    className="h-12 flex-1"
                    data-testid="manual-code-input"
                    onKeyDown={(e) => { if (e.key === 'Enter' && manualCode.trim()) handleManualStamp(); }}
                  />
                  <Button
                    onClick={handleManualStamp}
                    disabled={!manualCode.trim() || stamping}
                    className="h-12 px-5 rounded-xl text-white font-semibold"
                    style={{ backgroundColor: color }}
                    data-testid="manual-stamp-btn"
                  >
                    {stamping ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ScanLine className="h-4 w-4 mr-2" />}
                    Tamponner
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* CLIENTS TAB */}
      {activeTab === 'clients' && (
        <Card className="border-0 shadow-soft" data-testid="loyalty-clients">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Users className="h-5 w-5 text-orange-500" />Clients fidèles ({clients.length})</CardTitle></CardHeader>
          <CardContent>
            {clients.length === 0 ? (
              <div className="text-center py-8 text-gray-500"><Users className="h-12 w-12 mx-auto mb-3 text-gray-300" /><p>Aucun client fidèle pour le moment</p></div>
            ) : (
              <div className="space-y-2">
                {clients.map(card => (
                  <div key={card.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="font-medium text-gray-900">{card.client_name || card.client_email}</p>
                      <p className="text-xs text-gray-500">{card.client_email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {Array.from({ length: config?.stamps_required || 10 }).map((_, i) => (
                          <div key={i} className="h-3 w-3 rounded-full" style={{ backgroundColor: i < card.stamps ? color : '#e5e7eb' }} />
                        ))}
                      </div>
                      <Badge variant="outline" className="text-xs">{card.stamps}/{config?.stamps_required || 10}</Badge>
                      {card.completed_count > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">{card.completed_count}x</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'partage' && (() => {
        const baseUrl = window.location.origin;
        const shareUrl = `${baseUrl}/register?ref=${restaurantSlug}`;
        const copyLink = () => {
          navigator.clipboard.writeText(shareUrl).then(() => {
            setLinkCopied(true);
            toast.success('Lien copié !');
            setTimeout(() => setLinkCopied(false), 2500);
          }).catch(() => toast.error('Impossible de copier'));
        };
        return (
          <Card className="border-0 shadow-soft" data-testid="loyalty-share">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Share2 className="h-5 w-5 text-orange-500" />Partagez votre programme de fidélité
              </CardTitle>
              <CardDescription>Affichez ce QR code sur votre comptoir ou partagez le lien avec vos clients</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* QR Code */}
              <div className="flex flex-col items-center gap-4">
                <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100" data-testid="share-qr-code">
                  <QRCode value={shareUrl} size={220} level="H" />
                </div>
                <p className="text-sm text-gray-500 text-center max-w-sm">
                  Vos clients scannent ce code pour rejoindre votre programme de fidélité <strong>{restaurantName}</strong>
                </p>
              </div>

              {/* Lien partageable */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2"><Link className="h-4 w-4" />Lien à partager</Label>
                <div className="flex gap-2">
                  <Input value={shareUrl} readOnly className="bg-gray-50 text-sm font-mono" data-testid="share-link-input" />
                  <Button onClick={copyLink} variant={linkCopied ? "default" : "outline"} className={linkCopied ? "bg-emerald-500 hover:bg-emerald-600 text-white min-w-[110px]" : "min-w-[110px]"} data-testid="copy-link-btn">
                    {linkCopied ? <><Check className="h-4 w-4 mr-1" />Copié</> : <><Copy className="h-4 w-4 mr-1" />Copier</>}
                  </Button>
                </div>
              </div>

              {/* Conseils */}
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-orange-800">Conseils d'utilisation :</p>
                <ul className="text-sm text-orange-700 space-y-1 list-disc list-inside">
                  <li>Imprimez le QR code et placez-le sur votre comptoir ou vitrine</li>
                  <li>Partagez le lien via WhatsApp, SMS ou vos réseaux sociaux</li>
                  <li>Les clients pourront s'inscrire et rejoindre votre carte de fidélité</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};

export default LoyaltyManagement;
